import React, { useMemo, forwardRef, useImperativeHandle } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import { FileText, PieChart } from "lucide-react"
import { OvertimeRequest } from "@/types"
import { OvertimeStatus } from "@/types/enums"
import ExportButton from "@/components/ui/ExportButton"
import DonutChart, { DonutChartData } from "@/components/ui/DonutChart"

interface OvertimeReasonDonutChartProps {
  requests: OvertimeRequest[]
  title?: string
  isLoading?: boolean
  standalone?: boolean
  hasActiveFilters?: boolean
}

interface OvertimeReasonDonutChartHandle {
  download: () => void;
}

// Categorize justifications into common reasons
const categorizeReason = (justification: string): string => {
  const text = justification.toLowerCase();
  
  if (text.includes('urgent') || text.includes('emergency') || text.includes('critical')) {
    return 'Urgent/Emergency';
  }
  if (text.includes('deadline') || text.includes('due') || text.includes('schedule')) {
    return 'Project Deadline';
  }
  if (text.includes('staff') || text.includes('coverage') || text.includes('short') || text.includes('absent')) {
    return 'Staff Coverage';
  }
  if (text.includes('client') || text.includes('customer') || text.includes('meeting')) {
    return 'Client Requirements';
  }
  if (text.includes('maintenance') || text.includes('repair') || text.includes('fix')) {
    return 'Maintenance';
  }
  if (text.includes('training') || text.includes('learning') || text.includes('skill')) {
    return 'Training';
  }
  if (text.includes('peak') || text.includes('busy') || text.includes('volume') || text.includes('high demand')) {
    return 'Peak Period';
  }
  
  return 'Other';
};

const OvertimeReasonDonutChart = forwardRef<OvertimeReasonDonutChartHandle, OvertimeReasonDonutChartProps>(
  function OvertimeReasonDonutChart(
    { 
      requests, 
      title = "Overtime Reasons",
      isLoading = false,
      standalone = false,
      hasActiveFilters = false
    },
    ref
  ) {

  // Process overtime reason data from justification field (only approved requests)
  const reasonData = useMemo(() => {
    if (!requests?.length) return [];

    // Only process approved requests
    const approvedRequests = requests.filter(req => req.status === OvertimeStatus.APPROVED);
    if (!approvedRequests.length) return [];

    const reasonCounts = approvedRequests.reduce((acc, request) => {
      const reason = categorizeReason(request.justification || 'Other');
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate totals for percentages
    const total = approvedRequests.length;
    
    return Object.entries(reasonCounts)
      .filter(([, count]) => count > 0)
      .map(([reason, count]) => ({
        name: reason,
        value: count,
        percentage: ((count / total) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value); // Sort by count (descending)
  }, [requests]);

  // Convert data for DonutChart component
  const donutChartData = useMemo<DonutChartData[]>(() => {
    return reasonData.map((item, index) => ({
      name: item.name,
      value: item.value,
      fill: `hsl(var(--chart-${(index % 20) + 1}))`
    }));
  }, [reasonData]);

  // Create chart config for DonutChart
  const donutChartConfig = useMemo(() => {
    const config: Record<string, { label: string; color?: string }> = {
      value: {
        label: "Requests",
      },
    };
    
    reasonData.forEach((item, index) => {
      config[item.name] = {
        label: item.name,
        color: `hsl(var(--chart-${(index % 20) + 1}))`,
      };
    });
    
    return config;
  }, [reasonData]);

  // Calculate approved requests for center label
  const approvedRequests = useMemo(() => {
    return requests?.filter(req => req.status === OvertimeStatus.APPROVED).length || 0;
  }, [requests]);

  // Handle CSV download
  const handleDownload = () => {
    if (!reasonData.length) return;

    const headers = ["Reason", "Count", "Percentage"];
    const csvData = reasonData.map(item => [
      item.name,
      item.value.toString(),
      `${item.percentage}%`
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `overtime_reasons_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Chart content
  const chartContent = (
    <div className="h-72">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-pulse h-40 w-full bg-muted rounded-lg" />
        </div>
      ) : (
        <DonutChart
          data={donutChartData}
          chartConfig={donutChartConfig}
          isLoading={isLoading}
          showLegend={true}
          showCenterLabel={true}
          centerLabelValue={approvedRequests.toString()}
          centerLabelSubtext="Approved Request"
          formatValue={(value) => `${value} requests`}
          height={288}
          className="flex items-center justify-center"
        />
      )}
    </div>
  );
    
  useImperativeHandle(ref, () => ({
    download: handleDownload
  }));

  // Render with or without Card container based on standalone prop
  if (standalone) {
    return chartContent;
  }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow duration-200 h-[450px] flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b py-3 px-6">
        <CardTitle className="text-lg font-semibold text-left flex items-center">
          <FileText className="w-4 h-4 mr-2 text-primary" />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton 
            onClick={handleDownload} 
            disabled={!reasonData.length || isLoading} 
          />
        </div>
      </CardHeader>
      <CardContent className="pt-2 pb-4 flex-1 flex flex-col">
        <div className="h-72 flex-1">
          {donutChartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <PieChart className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {hasActiveFilters ? "No data matches your filters" : "No overtime reason data available"}
              </p>
              <p className="text-sm max-w-xs">
                {hasActiveFilters 
                  ? "Try adjusting the filters to see data"
                  : "Data will appear here once overtime requests are submitted"
                }
              </p>
            </div>
          ) : (
            <DonutChart
              data={donutChartData}
              chartConfig={donutChartConfig}
              isLoading={isLoading}
              showLegend={true}
              showCenterLabel={true}
              centerLabelValue={approvedRequests.toString()}
              centerLabelSubtext="Approved Requests"
              formatValue={(value) => `${value} requests`}
              height={288}
              innerRadius={80}
              outerRadius={120}
              className="flex items-center justify-center"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default OvertimeReasonDonutChart; 